import { Typography } from '@euler/app/components/typography';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { AppNavParams } from '@euler/app/Routes';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useOnNavigateBack } from '@euler/functions/useOnNavigateBack';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import React, { memo, ReactNode } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import {} from 'react-native-redash';
import { TaskHeaderMoreButton } from './HeaderOptions';
import { TaskNavBarSpinner } from './NavBarSpinner';

export const TaskNavBar = memo(
  ({
    title,
    vehicleName,
    licensePlateNo,
    scrollY,
    accessoryText,
    titleLoadingIndicator,
    rightOptions,
    colors = ['#006be4', '#5ba0e0'],
    onShowActions,
  }: {
    title: string;
    vehicleName: string;
    licensePlateNo: string;
    scrollY: SharedValue<number>;
    titleLoadingIndicator?: boolean;
    accessoryText?: string;
    rightOptions?: () => ReactNode;
    colors?: string[];
    onShowActions?: () => void;
  }) => {
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const { rpx } = useResponsiveContext();
    const {
      navBarHeight,
      safeAreaInsets,
      headerMaxExtend,
      offset,
      paddingTop,
    } = useTaskHeaderMetrics();

    const onBackPress = useOnNavigateBack(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.replace('Main', {});
      }
    });

    const title1Style = useAnimatedStyle(() => ({
      opacity: interpolate(
        scrollY.value,
        [-headerMaxExtend, 0, navBarHeight - offset],
        [1, 1, 0],
      ),
    }));

    const title2Style = useAnimatedStyle(() => ({
      opacity: interpolate(
        scrollY.value,
        [-headerMaxExtend, 0, navBarHeight - offset],
        [0, 0, 1],
      ),
    }));

    const shadeStyle = useAnimatedStyle(() => ({
      opacity: interpolate(
        scrollY.value,
        [-headerMaxExtend, 0, navBarHeight],
        [0, 0, 1],
      ),
    }));

    return (
      <>
        <Animated.View
          css={`
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            height: ${navBarHeight}px;
            padding-top: ${paddingTop}px;
            justify-content: space-between;
            align-items: center;
            flex-direction: row;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(16)}px;
            z-index: 10;
          `}
        >
          <Animated.View style={[StyleSheet.absoluteFill, shadeStyle]}>
            <LinearGradient
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 1 }}
              colors={colors}
              locations={[0.335, 1]}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
          <Animated.View
            css={`
              padding-top: ${safeAreaInsets.top}px;
              justify-content: center;
              align-items: center;
              flex-direction: row;
            `}
            style={[StyleSheet.absoluteFill, title1Style]}
          >
            <TaskNavBarSpinner animating={false} offsetX={-3} />
            <Typography.Label
              medium
              size={rpx(20)}
              color="white"
              numberOfLines={0}
            >
              {title}
            </Typography.Label>
            <TaskNavBarSpinner
              animating={titleLoadingIndicator === true}
              offsetX={3}
            />
          </Animated.View>
          <Animated.View
            css={`
              padding-top: ${safeAreaInsets.top}px;
              justify-content: center;
              align-items: center;
            `}
            style={[StyleSheet.absoluteFill, title2Style]}
          >
            <Typography.Label
              regular
              size={rpx(18)}
              color="white"
              css={`
                line-height: ${rpx(20)}px;
              `}
            >
              {vehicleName}
            </Typography.Label>
            <Typography.Label light size={rpx(12)} color="white">
              {kAppStoreSnapshotMode ? '沪AAAAAA' : licensePlateNo}
            </Typography.Label>
          </Animated.View>
          <Animated.View
            css={`
              position: absolute;
              left: 0px;
              right: 0px;
              bottom: 0px;
              height: ${navBarHeight - safeAreaInsets.top}px;
              justify-content: space-between;
              align-items: center;
              flex-direction: row;
              padding-left: ${rpx(15)}px;
              padding-right: ${rpx(16)}px;
            `}
          >
            <TouchableOpacity onPress={onBackPress}>
              <Icon
                name="arrow-back-circle-outline"
                size={rpx(32)}
                color="white"
              />
            </TouchableOpacity>
            {rightOptions != null ? (
              rightOptions()
            ) : onShowActions != null ? (
              <TaskHeaderMoreButton color="white" onPress={onShowActions} />
            ) : null}
            {accessoryText != null && (
              <View>
                <Typography.Label
                  light
                  size={rpx(11)}
                  color={Colors.White7_5}
                  css={`
                    text-align: center;
                  `}
                >
                  {accessoryText}
                </Typography.Label>
              </View>
            )}
          </Animated.View>
        </Animated.View>
      </>
    );
  },
);
